// TypeScript interfaces for Vandromeda License API

// License tier enum
export type LicenseTier = "free" | "pro_1" | "pro_5" | "agency";

// License status enum
export type LicenseStatus = "active" | "expired" | "revoked" | "invalid";

// Response from /api/license/validate
export interface LicenseValidationResponse {
  valid: boolean;
  tier: LicenseTier;
  expires_at: string;
  domains_remaining: number;
  error?: string;
}

// Response from /api/license/activate
export interface LicenseActivationResponse {
  success: boolean;
  license_info?: {
    tier: LicenseTier;
    expires_at: string;
    domains: string[];
    max_domains: number;
  };
  error?: string;
}

// Response from /api/license/deactivate
export interface LicenseDeactivationResponse {
  success: boolean;
  error?: string;
}

// License state for the application
export interface LicenseState {
  isLoading: boolean;
  isValidated: boolean;
  isPro: boolean;
  tier: LicenseTier;
  status: LicenseStatus;
  expiresAt: string | null;
  domainsRemaining: number;
  error: string | null;
}

// Initial license state
export const initialLicenseState: LicenseState = {
  isLoading: true,
  isValidated: false,
  isPro: false,
  tier: "free",
  status: "invalid",
  expiresAt: null,
  domainsRemaining: 0,
  error: null,
};

// Pro feature flags based on license tier
export interface ProFeatures {
  unlimitedRoutes: boolean;
  savedRoutes: boolean;
  multipleMapsPerPage: boolean;
  customStyling: boolean;
  prioritySupport: boolean;
  removeBranding: boolean;
}

// Map tier to features
export const getTierFeatures = (tier: LicenseTier): ProFeatures => {
  const proFeatures: ProFeatures = {
    unlimitedRoutes: true,
    savedRoutes: true,
    multipleMapsPerPage: true,
    customStyling: true,
    prioritySupport: true,
    removeBranding: true,
  };

  const freeFeatures: ProFeatures = {
    unlimitedRoutes: false,
    savedRoutes: false,
    multipleMapsPerPage: false,
    customStyling: false,
    prioritySupport: false,
    removeBranding: false,
  };

  return tier === "free" ? freeFeatures : proFeatures;
};

// Daily route limit for free tier
export const FREE_TIER_DAILY_ROUTE_LIMIT = 10;
